import { createListenerMiddleware, isAnyOf } from '@reduxjs/toolkit';
import {
  clearContextData,
  setAlarmSelected, setAutoAssignmentFromCall,
  setLastUpdate,
  setManualFromMyAlerts, setPollingDelay, showAlarmResolution,
  startPolling,
  stopPolling, toggleAlarmQueue
} from "./alarmQueueSlice";
import { getAlarmQueueAsync, getAlarmQueueLastUpdateAsync, loadAlarmContextData } from './alarmQueueThunks';
import type { RootState } from '../../app/store';
import { getExpedientQueueAsync } from '../expedientQueue/expedientQueueThunks';
import { findActiveCall } from './callLiveness';

const BASE_INTERVAL = 3000;
const MAX_INTERVAL = 15000;

export const alarmQueuePollingListener = createListenerMiddleware();

// Singleton para que el listener de stopPolling pueda abortar el loop que se
// arrancó desde otra entrada (api.cancelActiveListeners no cruza entradas).
let pollingController: AbortController | null = null;

function nextDelay(current: number) {
  return Math.min(current + BASE_INTERVAL, MAX_INTERVAL);
}

alarmQueuePollingListener.startListening({
  matcher: isAnyOf(startPolling),
  effect: async (_action, api) => {

    const state = api.getState() as RootState;

    pollingController?.abort();
    pollingController = new AbortController();
    const controller = pollingController;
    api.signal.addEventListener('abort', () => {
      controller.abort();
    });

    const user = (state as any).common?.user;

    // ⚠️ Prevent polling before login/session ready
    if (!user?.sacInfo?.sessionToken) {
      //console.warn('[Polling] SAC session not ready, skipping startPolling');
      return;
    }

    let delay = BASE_INTERVAL;
    let lastSeen = (state as any).alarmQueue.lastUpdate ?? null;

    try {
      // 🔹 Step 1: initial fetch
      //console.log('[Polling] Initial fetch...');
      const res = await api.dispatch(getAlarmQueueAsync()).unwrap();
      api.dispatch(getExpedientQueueAsync());

      if ((!res?.items?.length && !res?.myItems?.length)) {
        //console.warn("⚠ [Polling] Cola vacía tras primer fetch → recarga forzada");
        const second = await api.dispatch(getAlarmQueueAsync()).unwrap();

        const ts = second?.lastUpdate ?? Date.now();
        api.dispatch(setLastUpdate(ts));
        lastSeen = ts;
        //console.log('[Polling] Fetched initial lastUpdate via endpoint:', lastSeen);
      } else {
        const ts = res?.lastUpdate ?? Date.now();
        api.dispatch(setLastUpdate(ts));
        lastSeen = ts;
        //console.log('[Polling] Initial lastUpdate set to', ts);
      }

    } catch (e) {
      console.warn('[Polling] Initial fetch failed:', e);
    }

    // 🔁 Step 2: continuous polling
    while (!controller.signal.aborted) {
      try {
        // Los cambios de MIEMBROS de la conferencia (el operador entra, el llamado
        // descuelga) NO bumpean el lastUpdate en tmc-sac: ese timestamp solo lo
        // suben los triggers sobre la tabla ALERTS, no sobre CONFERENCES_MEMBERS.
        // Por eso, mientras el operador tiene una llamada activa seleccionada,
        // necesitamos el full fetch cada ciclo para mantener `conference.members`
        // fresco (si no, el botón de silenciar nunca llega a habilitarse).
        // La llamada en curso puede no ser la alerta seleccionada (el operador cambia el foco
        // a una medida del mismo expediente): se mira también en las working alerts.
        const alarmQueueState = ((api.getState() as RootState) as any).alarmQueue;
        const onActiveCall =
          alarmQueueState?.selectedAlarm?.callAlarm === true || !!findActiveCall(alarmQueueState?.myItems);

        if (onActiveCall) {
          // Vamos a full-fetch de todos modos → NOS SALTAMOS el poll ligero de
          // lastUpdate (sería redundante) y usamos el lastUpdate que ya devuelve
          // el propio full fetch.
          const full = await api.dispatch(getAlarmQueueAsync()).unwrap();
          api.dispatch(getExpedientQueueAsync());
          const ts = (full as any)?.lastUpdate;
          if (typeof ts === 'number') {
            api.dispatch(setLastUpdate(ts));
            lastSeen = ts;
          }
        } else {
          // Fuera de llamada: poll ligero y full fetch solo cuando sube lastUpdate.
          const result = await api.dispatch(getAlarmQueueLastUpdateAsync()).unwrap();
          const serverTs = result.lastUpdate;
          if (typeof serverTs === 'number' && (lastSeen == null || serverTs > lastSeen)) {
            //console.log('[Polling] Detected update, refreshing list...');
            await api.dispatch(getAlarmQueueAsync()).unwrap();
            api.dispatch(getExpedientQueueAsync());
            api.dispatch(setLastUpdate(serverTs));
            lastSeen = serverTs;
          }
        }

        delay = BASE_INTERVAL;
      } catch (err) {
        console.warn('[Polling] Error during loop:', err);
        delay = nextDelay(delay);
        api.dispatch(setPollingDelay({ delay: delay, max: delay >= MAX_INTERVAL }));
      }

      await new Promise<void>((resolve) => {
        const timeout = setTimeout(resolve, delay);
        controller.signal.addEventListener('abort', () => {
          clearTimeout(timeout);
          resolve();
        });
      });
    }
  },
});

alarmQueuePollingListener.startListening({
  matcher: isAnyOf(getAlarmQueueAsync.fulfilled),
  effect: async (action: ReturnType<typeof getAlarmQueueAsync.fulfilled>, api) => {

    const state = api.getState() as any;

    const operatorAlert = action.payload?.operatorAlert ?? null;
    const selectedAlert = state.alarmQueue?.selectedAlarm ?? null;

    // Flags actuales del slice
    const manual = state.alarmQueue?.manualSelectionPending ?? false;
    const autoCall = state.alarmQueue?.autoAssignmentFromCall ?? false;

    // Si ya hay alerta seleccionada, refrescamos su estado mutable (p. ej. estado de
    // mute/deaf de los miembros de la conferencia) desde el payload recién recibido,
    // preservando los campos añadidos en cliente como alarmTypeSelected.
    const refreshSelected = () => {
      if (!selectedAlert?.uuid) return;
      const updated =
        action.payload?.myItems?.find((a: any) => a.uuid === selectedAlert.uuid) ??
        action.payload?.items?.find((a: any) => a.uuid === selectedAlert.uuid);
      if (updated) {
        api.dispatch(
          setAlarmSelected({ ...updated, alarmTypeSelected: selectedAlert.alarmTypeSelected }),
        );
      }
    };

    // 1) Cambio manual entre mis alertas → NO abrir ventana ni cambiar selección
    if (manual && selectedAlert) {
      refreshSelected();
      return;
    }

    // 2) Si NO es manual y ya estoy atendiendo una alerta → no cambiar
    if (!manual && !autoCall && selectedAlert) {
      refreshSelected();
      return;
    }

    // 3) Si no hay operatorAlert en backend → no hacemos nada
    if (!operatorAlert) {
      return;
    }

    // 3b) Ventana de supresión tras aparcar/resolver: una respuesta EN VUELO anterior
    // al park aún trae operatorAlert y re-engancharía la cabecera de atención. La
    // auto-asignación por llamada la puentea (esa adopción es intencionada).
    const suppressedUntil = state.alarmQueue?.adoptionSuppressedUntil ?? 0;
    if (!autoCall && Date.now() < suppressedUntil) {
      return;
    }

    // 4) Reset flags si venimos de manual o llamada
    if (manual) api.dispatch(setManualFromMyAlerts(false));
    if (autoCall) api.dispatch(setAutoAssignmentFromCall(false));

    // 🧹 *** Limpieza contexto antes de cargar la nueva alerta ***
    api.dispatch(clearContextData());

    // 5) Asignar alerta activa del backend
    api.dispatch(setAlarmSelected(operatorAlert));

    // 6) Cargar datos de contexto vinculados a esa alerta
    api.dispatch(loadAlarmContextData(operatorAlert));

    // 7) SI es llamada (autoCall=true marcado desde postCallAlarm/postConsumeAlarm), abrir ventana
    if (autoCall) {
      api.dispatch(toggleAlarmQueue(true));
      api.dispatch(showAlarmResolution(true));
    }
  },
});

alarmQueuePollingListener.startListening({
  matcher: isAnyOf(stopPolling),
  effect: async () => {
    pollingController?.abort();
    pollingController = null;
  },
});
